import React from "react";
import {
    DataGrid,
} from "@mui/x-data-grid";
import {
    Box,
    Button,
    Typography,
    Chip,
    Modal,
    Hidden
} from "@mui/material";
import {
    CheckCircle,
} from 'react-feather';
import { useDispatch, useSelector } from "react-redux";
import SentBotmarkEstimateViewer from "./SentBotmarkEstimateViewer";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { invoiceTotalValueGetter } from '../../utils/columnValueGetters';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Page from '../../components/Page';
import { formatMoney } from '../../utils/moneyFormat';
import {
    useCompanyFilesInbox,
    useCustomerFiles,
} from '../../hooks/useFiles';
import { useCustomerInvoices } from '../../hooks/useInvoices';

function EstimatesToMeTable(){
    const company = useSelector((state) => state.CompanyReducer.userCompanies[0]);

    const startDateInitialState = dayjs().subtract(1, "years").startOf('day');
    const endDateInitialState = dayjs().endOf('day');
    const [startDate, setStartDate] = React.useState();
    const [endDate, setEndDate] = React.useState();
    const [exportStartDate, setExportStartDate] = React.useState(dayjs(startDateInitialState).format());
    const [exportEndDate, setExportEndDate] = React.useState(dayjs(endDateInitialState).format());

    function resetDate(){
        setStartDate(startDateInitialState);
        setEndDate(endDateInitialState);
        setExportStartDate(startDateInitialState);
        setExportEndDate(endDateInitialState);
    };

    const [paginationModel, setPaginationModel] = React.useState({pageSize: 25, page: 0});

    const { data: invoiceData, isFetching: isLoading } = useCustomerInvoices({
        companyId: company?.id,
        customerId: company?.id,
        startDate: exportStartDate,
        endDate: exportEndDate,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        enabled: Boolean(company?.id),
    });
    const { data: customerFilesData } = useCustomerFiles({
        companyId: company?.id,
        customerId: company?.id,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        enabled: Boolean(company?.id),
    });
    const { data: inboxFilesData } = useCompanyFilesInbox({
        companyId: company?.id,
        enabled: Boolean(company?.id),
    });

    const foundInvoiceCount = invoiceData?.foundTotalInvoiceCount ?? 0;
    const invoiceCount = invoiceData?.totalInvoiceCount ?? 0;
    const userInvoices = invoiceData?.invoicesDTO ?? [];

    const rowCountRef = React.useRef(foundInvoiceCount || 0);

    const rowCount = React.useMemo(() => {
      if (foundInvoiceCount !== undefined) {
        rowCountRef.current = foundInvoiceCount;
      }
      return rowCountRef.current;
    }, [foundInvoiceCount]);

    function applyInvoiceDateFilter() {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }

    const [invoiceModelObj, setInvoiceModelObj] = React.useState({});
    const [invoiceModal, setInvoiceModal] = React.useState(false);
    const handleInvoiceOpen = () => setInvoiceModal(true);
    const handleInvoiceClose = () => setInvoiceModal(false);
    function showInvoice(x){
        setInvoiceModelObj({
            created: x?.created,
            customerEmail: x?.customerEmail,
            customerId: x?.customerId,
            customerName: x?.customerName,
            developerId: x?.developerId,
            developerName: x?.developerName,
            estimateId: x?.estimateId,
            fileCompanyId: x?.fileCompanyId,
            fileId: x?.fileId,
            fileIsAccepted: x?.fileIsAccepted,
            fileName: x?.fileName,
            id: x?.id,
            invoiceValues: x?.invoiceValues,
            isInvoiceAccepted: x?.isInvoiceAccepted,
            isPaidFor: x?.isPaidFor,
            isProcessed: x?.isProcessed,
            isSent: x?.isSent,
            jobId: x?.jobId,
            openInvoice: x?.openInvoice,
            pointCount: x?.pointCount,
            reportUrl: x?.reportUrl,
            sequencingErrorCode: x?.sequencingErrorCode,
            summaryUrl: x?.summaryUrl,
            updated: x?.updated,
            pointList: x?.pointList,
        })
        handleInvoiceOpen();
    };

    /// For Creating and Downloading Invoice as CSV //
    function exportToCsv() {
        let csvDownloadable = [];
        let exportECU = 0;
        let exportFinalTotal = 0;
        let exportStartDateFormat = dayjs(exportStartDate).format('MM/DD/YYYY');
        let exportEndDateFormat = dayjs(exportEndDate).format('MM/DD/YYYY');

        csvDownloadable.push(
            [`Date`,`From`,`Plan`,`Point Total`,`Sale Total`]
        );

        for (let i = 0; i < userInvoices.length; i++){
            let data = [];
            let invoiceTotal = 0;
            let taxTotal = 0;
            let finalTotal = 0;
            if (userInvoices[i]?.invoiceValues != undefined) {
                data = JSON?.parse(userInvoices[i]?.invoiceValues)

                for (let i = 0; i < data.items.length; i++) {
                    invoiceTotal += data.items[i].total;
                }
                for (let i = 0; i < data.taxes.length; i++) {
                    taxTotal += Number(data.taxes[i].total);
                }
                finalTotal = taxTotal+invoiceTotal;

                exportFinalTotal += finalTotal;

                if(data.pointTypeTotal){
                    exportECU += data.pointTypeTotal;
                }
            };

            if(userInvoices[i].developerName == company.name){
                csvDownloadable.push(
                    [`${data.date}`,`Botmark`,`${userInvoices[i].fileName.split('.')[0]}`,`${data?.pointTypeTotal}`,`${formatMoney(finalTotal)}`]
                )
            } else {
                csvDownloadable.push(
                    [`${data.date}`,`${userInvoices[i].developerName}`,`${userInvoices[i].fileName.split('.')[0]}`,`${data?.pointTypeTotal}`,`${formatMoney(finalTotal)}`]
                )
            }
        }

        csvDownloadable.push(
            [`${exportStartDateFormat} - ${exportEndDateFormat}`,``,``,`${exportECU}`,`${formatMoney(exportFinalTotal)}`]
        );

        let csvContent = csvDownloadable.map(e => e.join(",")).join("\n");

        var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        if (navigator.msSaveBlob) { // IE 10+
            navigator.msSaveBlob(blob, `allReceivedEstimates-${exportStartDateFormat}-${exportEndDateFormat}`);
        } else {
            var link = document.createElement("a");
            if (link.download !== undefined) { // feature detection
                // Browsers that support HTML5 download attribute
                var url = URL.createObjectURL(blob);
                link.setAttribute("href", url);
                link.setAttribute("download", `allReceivedEstimates-${exportStartDateFormat}-${exportEndDateFormat}`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    };

    const columns = [
        { field: 'developerName', headerName: 'Sender', width: 200, headerClassName: 'header', editable: false,renderCell: (params) =>{
            if (params.row.developerName == company.name){
                return <span>Botmark</span>
            } else {
                return <span>{params.row.developerName}</span>
            }
        }},
        { field: 'fileName', headerName: 'Project Name', width: 250, headerClassName: 'header', editable: false},
        { field: 'invoice', headerName: 'Estimate', width: 200, headerClassName: 'header', sortable: false, renderCell: (params) => (
            Invoice(params.row)
        )},
        { field: 'total', headerName: 'Total', width: 100, headerClassName: 'header', valueGetter: invoiceTotalValueGetter,
            renderCell: (params) => {
            var data = JSON?.parse(params.row?.invoiceValues);

            return (<div>$ {data?.finalTotal}</div>)
        }},
        { field: 'created', headerName: 'Created', width: 200, headerClassName: 'header', renderCell: (params) => {
            let date = new Date(params.row.created);
            return date.toLocaleString()
        }},
    ];

    function Invoice(x) {
        return (
            // download invoice
            <div>
                <Button variant="contained" color="success" onClick={() => showInvoice(x)}>
                    View/ Download
                </Button>
            </div>
        )
    }
    const rows = userInvoices;

    return (
        <Page title="Recvieved Invoices">
        <Box style={{paddingRight:'30px',height: '70vh'}}>
            <Box sx={{ marginTop: "10px", marginBottom: "10px" }}>
                <Typography textAlign={"center"} color={"blue"} variant='h4'>
                    Received Invoices
                </Typography>
                <Typography textAlign={"center"}>
                    Total Invoices: {invoiceCount}
                </Typography>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box style={{display:'flex', flexDirection:'row'}}>
                    <Box sx={{ m: 1 }}>
                        <DatePicker
                            views={['year', 'month', 'day']}
                            className='DatePicker'
                            label="Start Date"
                            value={startDate}
                            onChange={ (selectedValue) => {
                                setStartDate(dayjs(selectedValue).startOf('day'));
                                setExportStartDate(dayjs(selectedValue).startOf('day').format());
                            }}
                            renderInput={ (params) => <TextField {...params} />}
                        />
                    </Box>

                    <Box sx={{ m: 1 }} >
                        <DatePicker
                            className='DatePicker'
                            label="End Date"
                            value={endDate}
                            onChange={ (selectedValue) => {
                                setEndDate(dayjs(selectedValue).endOf('day'));
                                setExportEndDate(dayjs(selectedValue).endOf('day').format());
                            }}
                            renderInput={ (params) => <TextField {...params} />}
                        />
                    </Box>
                    <Button variant='contained' style={{height:'75%', alignSelf:'center', margin:'auto'}} onClick={applyInvoiceDateFilter}>Filter Date Range</Button>
                    <Button variant='outlined' color='warning' style={{height:'75%', alignSelf:'center', margin:'auto'}} onClick={()=>{resetDate()}}>Reset Date Range</Button>
                    <Button variant='contained' color='secondary' style={{height:'75%', alignSelf:'center', margin:'auto'}} onClick={()=>{exportToCsv()}}>Export Invoice Data</Button>
                </Box>
            </LocalizationProvider>
            <DataGrid
                columns={columns}
                rows={rows}
                loading={isLoading}
                pageSizeOptions={[10, 25, 50, 100]}
                rowCount={rowCount}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                style={{textAlign:'left'}}
            />
            <Modal
                className="invoiceModal"
                open={invoiceModal}
                onClose={handleInvoiceClose}
                hideBackdrop={true}
            >
                <div>
                    <Button onClick={() => setInvoiceModal(false)}>Close</Button>
                    <SentBotmarkEstimateViewer className="estimateDiv" item={invoiceModelObj} handleInvoiceClose={handleInvoiceClose}/>
                </div>
            </Modal>
        </Box>
        </Page>
    )
}

export default EstimatesToMeTable;